import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * How long hover activation may be held back while waiting for the submenu content to
 * settle. Purely a safety net: if one of the signals never arrives, the menu still works.
 */
const READY_TIMEOUT = 3000;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Whether the submenus are allowed to open on hover yet. See `#prepareForInteraction`.
   */
  #ready = false;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #readyTimeoutTimer;

  /**
   * @type {AbortController | null}
   */
  #readyAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerenter', this.#cancelOverflowDeactivate);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    this.dataset.menuReady = 'false';
    this.#prepareForInteraction();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.#cancelOverflowDeactivate();
    this.overflowMenu?.removeEventListener('pointerenter', this.#cancelOverflowDeactivate);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#readyTimeoutTimer);
    this.#readyAbortController?.abort();
    this.#readyAbortController = null;
  }

  /**
   * Hold hover activation back until the submenu content is actually there to be measured.
   *
   * Opening a submenu writes `--submenu-height` from a single synchronous `offsetHeight`
   * read. Immediately after a page load that read happens while the subtree is still
   * settling -- images are in flight and the header section has not been hydrated yet -- so
   * the panel opens to a stale, too-short height and the content looks sliced in half.
   * Waiting a couple of seconds before hovering has always worked, so instead of guessing at
   * a delay we wait for the same signals and only then arm the hover.
   *
   * `[data-menu-ready="false"]` is exposed on the element so this state is visible in the
   * DOM inspector.
   */
  async #prepareForInteraction() {
    this.#readyAbortController = new AbortController();
    const { signal } = this.#readyAbortController;

    // A missing signal must never leave the menu permanently inert, so cap the wait.
    const budget = new Promise((resolve) => {
      this.#readyTimeoutTimer = setTimeout(resolve, READY_TIMEOUT);
    });

    await Promise.race([this.#whenContentSettled(signal), budget]);

    if (signal.aborted) return;

    clearTimeout(this.#readyTimeoutTimer);

    this.#warmUpSubmenus();

    this.#ready = true;
    this.dataset.menuReady = 'true';

    // The pointer may already be resting on a menu item whose hover we swallowed.
    this.#activateHoveredItem();
  }

  /**
   * Resolves once everything the submenus are measured from has settled.
   *
   * @param {AbortSignal} signal
   */
  async #whenContentSettled(signal) {
    // 1. All render-blocking resources and the initial images are done.
    await new Promise((resolve) => onDocumentLoaded(resolve));
    if (signal.aborted) return;

    // 2. The slideshow inside the slider mega menu can lay itself out.
    await customElements.whenDefined('slideshow-component');
    if (signal.aborted) return;

    // Lay the submenus out already: a `content-visibility: auto` subtree that is being
    // skipped never starts loading the images inside it, so without this step 4 below would
    // sit and wait for the whole budget.
    this.#warmUpSubmenus();

    // 3. The header section has been re-rendered by section-hydration.js, which is what
    //    replaces the menu markup after load.
    await this.#whenSectionHydrated(signal);
    if (signal.aborted) return;

    // 4. The mega menu artwork has decoded, so the cards have their final height.
    await this.#whenSubmenuImagesSettled(signal);
    if (signal.aborted) return;

    // 5. Give the browser two frames to flush the layout that all of the above triggered.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  /**
   * Resolves when the surrounding section has been hydrated (or immediately when nothing
   * will hydrate it, as in the theme editor).
   *
   * @param {AbortSignal} signal
   */
  #whenSectionHydrated(signal) {
    const section = /** @type {HTMLElement | null} */ (this.closest('.shopify-section'));

    // `section-hydration.js` is not rendered in design mode, so nothing would ever flip this.
    if (!section || window.Shopify?.designMode) return Promise.resolve();
    if (section.dataset.hydrated === 'true') return Promise.resolve();

    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (section.dataset.hydrated !== 'true') return;
        observer.disconnect();
        resolve(undefined);
      });

      observer.observe(section, { attributes: true, attributeFilter: ['data-hydrated'] });
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    });
  }

  /**
   * Resolves when every image inside the submenus has either decoded or failed.
   *
   * @param {AbortSignal} signal
   */
  #whenSubmenuImagesSettled(signal) {
    const images = /** @type {HTMLImageElement[]} */ ([
      ...this.querySelectorAll('[ref="submenu[]"] img'),
    ]).filter((image) => !image.complete);

    if (!images.length) return Promise.resolve();

    return Promise.all(
      images.map(
        (image) =>
          new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true, signal });
            image.addEventListener('error', resolve, { once: true, signal });
          })
      )
    );
  }

  /**
   * Lay every submenu out once while it is still invisible to the user.
   *
   * The submenus are `content-visibility: auto`, so their subtree is not laid out at all
   * until something reveals them -- which is why the very first hover measures a box that
   * has never had a layout pass. Doing that pass up front, behind `visibility: hidden` and a
   * zero `--submenu-height`, means the first real hover measures settled content.
   */
  #warmUpSubmenus() {
    const submenus = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[ref="submenu[]"]')]);

    for (const submenu of submenus) {
      const wasActive = submenu.dataset.active != null;

      // `[data-active]` is what flips the submenu off `content-visibility: auto`.
      submenu.dataset.active = '';

      // Reading a layout property forces the pass we are after. The submenu stays
      // `visibility: hidden` throughout, so nothing appears on screen.
      void submenu.offsetHeight;

      if (!wasActive) delete submenu.dataset.active;
    }
  }

  /**
   * Open the submenu of the item the pointer is already resting on, if any.
   */
  #activateHoveredItem() {
    const hoveredItem = this.querySelector('.menu-list__list-item:hover');
    if (!hoveredItem) return;

    hoveredItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#scheduleOverflowDeactivate();
  };

  #cancelOverflowDeactivate = () => {
    clearTimeout(this.#overflowDeactivateTimer);
    this.#overflowDeactivateTimer = undefined;
  };

  #scheduleOverflowDeactivate() {
    this.#cancelOverflowDeactivate();
    this.#overflowDeactivateTimer = setTimeout(() => {
      if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;
      this.#deactivate();
    }, 75);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #overflowDeactivateTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Swallow hover until the submenu content has settled, otherwise the panel opens to a
    // height measured from a subtree that is still loading. Keyboard focus is always let
    // through -- never trap a keyboard user behind a loading state.
    if (!this.#ready && !(event instanceof FocusEvent)) return;

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#cancelOverflowDeactivate();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      (event.relatedTarget.closest('[slot="overflow"]') != null ||
        this.overflowMenu?.contains(event.relatedTarget));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    const isLeavingMore = event.target.closest('[slot="more"]') != null;
    const isOverflowOpen = this.dataset.overflowExpanded === 'true';

    if (isLeavingMore || isOverflowOpen) {
      this.#scheduleOverflowDeactivate();
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
